import { useEffect, useState } from 'react';
import { getImagePath } from '../../../Utils/assetUtils';
import { Typography } from 'antd';
import styles from './AssetLogo.module.css';

type AssetLogoProps = React.ImgHTMLAttributes<HTMLImageElement> & {
  imageId?: string;
  assetType?:
    | 'BANK'
    | 'FIP'
    | 'MUTUALFUND_ISIN'
    | 'MUTUAL_FUND'
    | 'MERCHANT_LOGO'
    | 'EQUITY_ISIN';
  fallback: {
    path: string;
    type: 'IMAGE' | 'INITIALS';
    className: string;
  };
};

const AssetLogo = ({
  imageId,
  assetType,
  fallback,
  ...props
}: AssetLogoProps) => {
  const {
    path: fallbackPath,
    type: fallbackType,
    className: fallbackClassName,
  } = fallback;
  const imageExtenstion = () => {
    switch (assetType) {
      case 'BANK':
      case 'FIP':
      case 'MUTUAL_FUND':
        return 'jpeg';
      case 'MUTUALFUND_ISIN':
      case 'EQUITY_ISIN':
        return 'png';
      case 'MERCHANT_LOGO':
        return 'jpg';
      default:
        return '';
    }
  };
  const resolvedSrc =
    props.src ?? `https://logo.ignosis.ai/${imageId}.${imageExtenstion()}`;
  const [imgSrc, setImgSrc] = useState<string>(resolvedSrc);
  const [hasError, setHasError] = useState<boolean>(false);

  useEffect(() => {
    setImgSrc(resolvedSrc);
    setHasError(false);
  }, [resolvedSrc]);

  const handleError = () => {
    if (fallbackType === 'INITIALS') {
      setHasError(true);
      return;
    }
    const fallback = getImagePath(fallbackPath);
    if (imgSrc !== fallback) {
      setImgSrc(fallback);
    }
  };

  const getInitials = () => {
    const words = fallbackPath.split(' ').filter(Boolean);
    return words.length >= 2
      ? (words[0][0] + words[1][0]).toUpperCase()
      : fallbackPath.substring(0, 2).toUpperCase();
  };

  if (hasError && fallbackType === 'INITIALS') {
    return (
      <Typography.Text
        className={`${styles.defaultInitials} ${fallbackClassName ?? ''}`}
      >
        {getInitials()}
      </Typography.Text>
    );
  }

  return (
    <img
      {...props}
      alt={props.alt ?? ''}
      src={imgSrc}
      onError={handleError}
      loading='lazy'
    />
  );
};

export default AssetLogo;
